/**
 * ADR-0185 Addendum 4 — the model home (`/app/optimize/:modelId`), every
 * role, two tabs. **Board** is the default — the existing board frame,
 * mounted exactly as-is. **Model** (`?tab=model`) is an INFORMATIONAL,
 * read-only description of the model's shape: what work it schedules and
 * from where, its setup-key components, class mapping, promise settings and
 * disruptions — with live bindings and data freshness. Nothing on it renders
 * as an input; people never manually configure an optimize model (the
 * doctrine's "sessions author, humans review" applied to the surface).
 *
 * The few genuine human gestures — solve budgets, active/disabled, delete,
 * sharing scope — live in a visually separate, quiet settings area at the
 * FOOT of the Model tab, builder/admin only. Operational knobs, not model
 * configuration; their existing behaviors (CAS, blur-save) are kept.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";
import { ScopeHeader } from "../components/ScopeHeader.js";
import { useToast } from "../components/Toast.js";
import { formatTimestamp, formatTimestampOr } from "../lib/formatTime.js";
import { useMyGroups, type ContentScope } from "../lib/scope.js";
import { canManageModels, modelHomeTabFromSearch } from "../lib/optimizeNav.js";
import { OptimizeBoardFrame, OptimizeNextUpFrame } from "./OptimizeBoard.js";

// ── served template projection (the hub owns this vocabulary) ──────────────

type CardField = { field: string; label: string; required: boolean; semantic: string };
/** `optional` (Amendment 4): unbound, the card contributes nothing — worded as such, never the generic "not connected yet". */
type CardRead = { label: string; description: string; fields: CardField[]; optional?: boolean };
type CardSetting = {
  key: string;
  label: string;
  kind: "components" | "class_patterns" | "number" | "toggle";
  help?: string;
  min_count?: number;
  max_count?: number;
  default?: number;
};
type AcceptedDisruption = { kind: string; label: string; description: string; subject_required: string[]; subject_optional: string[] };
type Card = {
  block_kind: string;
  title: string;
  what_it_does: string;
  reads: CardRead | null;
  settings: CardSetting[];
  notes: string[];
  accepts?: AcceptedDisruption[];
};
type TemplateProjection = { id: string; display_name: string; description: string; version: number; cards: Card[] };

type ClassPattern = { same_part: boolean; components: boolean[] };
type SetupKeyComponent = { column: string; label: string; basis_column?: string };
type Binding = { operation_id?: string; node_id?: string; columns?: Record<string, string> };
type Block = {
  block_kind: string;
  bindings: Binding;
  config: {
    components?: SetupKeyComponent[];
    class_patterns?: Record<string, ClassPattern>;
    emphasis?: number;
    hard_promises?: boolean;
  };
};

type Model = {
  id: string;
  name: string;
  template_id: string;
  work: Binding;
  blocks: Block[];
  solve_time_budget_ms: number | null;
  interactive_budget_ms: number | null;
  status: "active" | "disabled";
  scope: ContentScope;
  owner_account_id: string | null;
  owner_group_id: string | null;
  owner_group_name?: string | null;
  updated_at: string;
};

/**
 * Addendum 5 — the model GET's viewer-readable bindings projection: resolved
 * names + per-binding freshness, served for every role. The tab renders from
 * these facts alone — no builder-gated reads.
 */
type ModelBindingView = {
  card: string;
  unbound: boolean;
  operation_name: string | null;
  operation_status: string | null;
  operation_missing: boolean;
  step_label: string | null;
  output: { columns: string[]; last_run_at: string | null; row_count: number | null } | null;
  /** Amendment 4 — the template declares this read optional. */
  optional?: boolean;
};

type Overlay = {
  id: string;
  kind: string;
  subject: Record<string, unknown>;
  starts_at: string;
  ends_at: string | null;
  note: string | null;
  created_at: string;
  cleared_at: string | null;
};

type ProposalListItem = {
  id: string;
  version: number;
  kind: "solve" | "what_if";
  diff: { hours_saved?: number } | null;
  created_at: string;
};

const COST_LABEL_REASONS: Record<string, string> = {
  costs_unbound: "The changeover-costs table is not connected yet — the class labels come from its data.",
  no_stored_output: "The connected step has no stored output yet — the class labels arrive when the pipeline runs.",
  output_unreadable: "The stored output feeding the class labels could not be read back.",
  label_column_missing: "The mapped class-label column is no longer in the connected output.",
  template_missing: "This model's template is no longer shipped on this platform.",
};

/** Render a disruption entry's subject as a sentence fragment, never raw JSON. */
function subjectText(kind: string, subject: Record<string, unknown>): string {
  const s = (k: string) => (typeof subject[k] === "string" ? String(subject[k]) : "");
  switch (kind) {
    case "operator_absence":
      return s("operator_label");
    case "machine_unavailable":
      return s("machine");
    case "tooling_unavailable":
      return s("tooling_note") ? `${s("machine")} — ${s("tooling_note")}` : s("machine");
    case "job_pin":
      return `job ${s("job")}`;
    default:
      return Object.values(subject).filter((v) => typeof v === "string").join(", ");
  }
}

// ── the informational Model tab ────────────────────────────────────────────

function ModelDescription(props: {
  model: Model;
  template: TemplateProjection | null;
  /** The served names/freshness projection — the tab's one source for binding facts (every role). */
  bindings: ModelBindingView[];
  canManage: boolean;
  onModelChanged: (model: Model) => void;
  onReload: () => void | Promise<void>;
}) {
  const { model, template } = props;

  const [costLabels, setCostLabels] = useState<string[]>([]);
  const [costLabelsReason, setCostLabelsReason] = useState<string | null>(null);
  const [costLabelsAsOf, setCostLabelsAsOf] = useState<string | null>(null);
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [showCleared, setShowCleared] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ labels: string[]; reason?: string; last_run_at?: string | null }>(
          `/api/tenant/optimize/models/${model.id}/cost-labels`
        );
        setCostLabels(data.labels);
        setCostLabelsReason(data.reason ?? null);
        setCostLabelsAsOf(data.last_run_at ?? null);
      } catch {
        /* the served reason text covers the unbound state */
      }
    })();
    void (async () => {
      try {
        const data = await apiFetch<{ overlays: Overlay[] }>(
          `/api/tenant/optimize/models/${model.id}/overlays?include_cleared=1`
        );
        setOverlays(data.overlays);
      } catch {
        /* degrade to the empty line */
      }
    })();
  }, [model.id]);

  const changeover = model.blocks.find((b) => b.block_kind === "changeover_costs");
  const components = changeover?.config.components ?? [];
  const savedPatterns = changeover?.config.class_patterns ?? {};

  /**
   * The uniform "what it reads" section, described rather than edited — all
   * binding facts from the served projection, identical for every role.
   */
  const renderRead = (card: Card, binding: Binding) => {
    const read = card.reads!;
    const bv = props.bindings.find((b) => b.card === card.block_kind) ?? null;
    const schemaNames = new Set(bv?.output?.columns ?? []);
    const columns = Object.entries(binding.columns ?? {}).filter(([, column]) => !!column);

    const findings: string[] = [];
    if (bv?.operation_missing) {
      findings.push("The connected pipeline no longer exists — connect a new data source before solving.");
    }
    if (bv?.operation_status === "validate") {
      findings.push(`"${bv.operation_name ?? "The connected pipeline"}" needs validation before it can feed a solve.`);
    }
    if (bv && !bv.unbound && !bv.operation_missing && !bv.output) {
      findings.push("The connected step has no stored output — the pipeline has not run yet.");
    }
    for (const [field, column] of columns) {
      if (schemaNames.size > 0 && !schemaNames.has(column)) {
        const label = read.fields.find((f) => f.field === field)?.label ?? field;
        findings.push(`"${column}" is no longer a column of the connected output (mapped to ${label}).`);
      }
    }

    return (
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        <div className="help">{read.description}</div>
        {findings.map((f, i) => (
          <div className="help" key={i} style={{ color: "var(--danger-text)" }}>
            {f}
          </div>
        ))}
        {!binding.operation_id ? (
          <div className="help">
            {read.optional
              ? "Not connected — machine moves aren't limited by the routing until it is."
              : "Not connected to a data source yet."}
          </div>
        ) : bv?.operation_name ? (
          <div>
            Reads <strong>{bv.operation_name}</strong>
            {bv.step_label ? (
              <>
                {" "}· step <strong>{bv.step_label}</strong>
              </>
            ) : null}
          </div>
        ) : null}
        {columns.length > 0 ? (
          <div style={{ display: "grid", gap: 4, gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
            {columns.map(([field, column]) => {
              const f = read.fields.find((x) => x.field === field);
              return (
                <div key={field} title={f?.semantic}>
                  <span className="muted">{f?.label ?? field}:</span> <span className="mono">{column}</span>
                </div>
              );
            })}
          </div>
        ) : null}
        {bv?.output ? (
          <div className="help">
            Data as of {formatTimestampOr(bv.output.last_run_at, "never run")}
            {bv.output.row_count != null ? ` — ${bv.output.row_count} rows` : ""}
          </div>
        ) : null}
      </div>
    );
  };

  /** The changeover card's shape: the ordered components + the class mapping. */
  const renderChangeoverShape = (card: Card) => {
    const componentsSetting = card.settings.find((s) => s.kind === "components");
    const mappingSetting = card.settings.find((s) => s.kind === "class_patterns");
    const mappingRows = [...new Set([...costLabels, ...Object.keys(savedPatterns)])].sort();
    const hasFullChangeover = Object.values(savedPatterns).some(
      (p) => !p.same_part && p.components.length === components.length && p.components.every((c) => !c)
    );
    return (
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        <span className="label">{componentsSetting?.label ?? "Setup-key components"}</span>
        {componentsSetting?.help ? <div className="help">{componentsSetting.help}</div> : null}
        {components.length === 0 ? (
          <div className="help">No components chosen yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 4 }}>
            {components.map((c, i) => (
              <div key={i}>
                {i + 1}. <strong>{c.label || c.column}</strong>{" "}
                <span className="muted">
                  — column <span className="mono">{c.column}</span>
                  {c.basis_column ? (
                    <>
                      , basis <span className="mono">{c.basis_column}</span>
                    </>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        )}

        <span className="label" style={{ marginTop: 4 }}>{mappingSetting?.label ?? "Class mapping"}</span>
        {mappingSetting?.help ? <div className="help">{mappingSetting.help}</div> : null}
        {mappingRows.length === 0 ? (
          <div className="help">
            {COST_LABEL_REASONS[costLabelsReason ?? ""] ?? "No class labels yet."}
          </div>
        ) : (
          <>
            <div className="tableWrap">
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Same part?</th>
                    {components.map((c, i) => (
                      <th key={i}>{c.label || c.column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mappingRows.map((label) => {
                    const p = savedPatterns[label];
                    const staleLabel = !costLabels.includes(label) && costLabels.length > 0;
                    return (
                      <tr key={label}>
                        <td>
                          {label}
                          {staleLabel ? (
                            <span className="pill pillWarn" style={{ marginLeft: 6 }}>Not in the cost data</span>
                          ) : null}
                        </td>
                        <td>{p ? (p.same_part ? "Same part" : "Different part") : <span className="muted">Not mapped</span>}</td>
                        {components.map((_, i) => (
                          <td key={i}>
                            {p && p.components[i] != null ? (p.components[i] ? "Same" : "Different") : <span className="muted">—</span>}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {costLabelsAsOf ? (
              <div className="help">Class labels as of {formatTimestamp(costLabelsAsOf)}.</div>
            ) : null}
            {!hasFullChangeover && components.length > 0 ? (
              <div className="help" style={{ color: "var(--danger-text)" }}>
                No class is mapped to the full-changeover pattern (different part, every component different) —
                it prices the conservative fallback for unmeasured patterns.
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  /** The promise card's shape: emphasis + hard promises, as facts. */
  const renderPromiseShape = (card: Card) => {
    const promise = model.blocks.find((b) => b.block_kind === "promise_protection");
    const emphasisSetting = card.settings.find((s) => s.key === "emphasis");
    const hardSetting = card.settings.find((s) => s.key === "hard_promises");
    return (
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        {emphasisSetting ? (
          <div>
            <span className="muted">{emphasisSetting.label}:</span>{" "}
            <strong>{promise?.config.emphasis ?? emphasisSetting.default ?? 0}</strong>
            {emphasisSetting.help ? <div className="help">{emphasisSetting.help}</div> : null}
          </div>
        ) : null}
        {hardSetting ? (
          <div>
            <span className="muted">{hardSetting.label}:</span>{" "}
            <strong>{promise?.config.hard_promises === true ? "Yes" : "No"}</strong>
            {hardSetting.help ? <div className="help">{hardSetting.help}</div> : null}
          </div>
        ) : null}
      </div>
    );
  };

  /** The disruptions card: what it accepts, and what is marked right now. */
  const renderDisruptions = (card: Card) => {
    const accepts = card.accepts ?? [];
    const activeOverlays = overlays.filter((o) => !o.cleared_at);
    const clearedOverlays = overlays.filter((o) => o.cleared_at);
    return (
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        <div className="help">Accepts: {accepts.map((k) => k.label).join(" · ")}</div>
        {activeOverlays.length === 0 ? (
          <div className="help">
            Nothing marked. Absences, outages, and pins recorded on the board shape every solve while active.
          </div>
        ) : (
          <div className="tableWrap">
            <table className="table tableCompact">
              <thead>
                <tr>
                  <th>What</th>
                  <th>Who / what</th>
                  <th>Window</th>
                  <th>Recorded</th>
                </tr>
              </thead>
              <tbody>
                {activeOverlays.map((o) => (
                  <tr key={o.id}>
                    <td>{accepts.find((k) => k.kind === o.kind)?.label ?? o.kind}</td>
                    <td>{subjectText(o.kind, o.subject)}</td>
                    <td>
                      {formatTimestamp(o.starts_at)} → {o.ends_at ? formatTimestamp(o.ends_at) : "until cleared"}
                    </td>
                    <td>{formatTimestamp(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {clearedOverlays.length > 0 ? (
          <div>
            <button className="disclosureToggle" aria-expanded={showCleared} onClick={() => setShowCleared((v) => !v)}>
              {showCleared ? "▾" : "▸"} Cleared history ({clearedOverlays.length})
            </button>
            {showCleared ? (
              <div className="tableWrap">
                <table className="table tableCompact">
                  <tbody>
                    {clearedOverlays.map((o) => (
                      <tr key={o.id}>
                        <td>{accepts.find((k) => k.kind === o.kind)?.label ?? o.kind}</td>
                        <td>{subjectText(o.kind, o.subject)}</td>
                        <td className="muted">cleared {formatTimestampOr(o.cleared_at, "")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  /** One card of the description — served prose first, then the live shape. */
  const renderCard = (card: Card) => {
    const isWork = card.block_kind === "work";
    const block = isWork ? null : model.blocks.find((b) => b.block_kind === card.block_kind);
    const binding = isWork ? model.work : block?.bindings ?? {};
    return (
      <div className="card" key={card.block_kind}>
        <strong>{card.title}</strong>
        <div className="help">{card.what_it_does}</div>
        {card.reads ? renderRead(card, binding) : null}
        {card.block_kind === "changeover_costs" ? renderChangeoverShape(card) : null}
        {card.block_kind === "promise_protection" ? renderPromiseShape(card) : null}
        {card.block_kind === "disruptions" ? renderDisruptions(card) : null}
        {card.notes.map((n, i) => (
          <div className="help" key={i} style={{ marginTop: 8 }}>
            {n}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {template ? (
        <div className="card">
          <strong>{template.display_name}</strong>
          <div className="help">{template.description}</div>
        </div>
      ) : (
        <div className="card">
          <strong>Template unavailable</strong>
          <div className="help">
            The template this model was created from is no longer shipped on this platform. Templates ship
            with the platform — contact the platform operator.
          </div>
        </div>
      )}

      {(template?.cards ?? []).map(renderCard)}

      {props.canManage ? (
        <AdminStrip model={model} onModelChanged={props.onModelChanged} onReload={props.onReload} />
      ) : null}
    </div>
  );
}

// ── the quiet admin strip (builder/admin only) ─────────────────────────────

function AdminStrip(props: {
  model: Model;
  onModelChanged: (model: Model) => void;
  onReload: () => void | Promise<void>;
}) {
  const toast = useToast();
  const { groups: callerGroups } = useMyGroups();
  const { model } = props;

  const [me, setMe] = useState<{ id: string; role: "admin" | "builder" | "viewer" } | null>(null);
  const [proposals, setProposals] = useState<ProposalListItem[]>([]);
  const [standingBudgetS, setStandingBudgetS] = useState(
    model.solve_time_budget_ms == null ? "" : String(Math.round(model.solve_time_budget_ms / 1000))
  );
  const [interactiveBudgetS, setInteractiveBudgetS] = useState(
    model.interactive_budget_ms == null ? "" : String(Math.round(model.interactive_budget_ms / 1000))
  );
  const [statusBusy, setStatusBusy] = useState(false);
  const [solveBusy, setSolveBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const meData = await apiFetch<{ user: { id: string; entitlement?: string; role?: string } }>("/api/tenant/me");
        const ent = meData.user.entitlement ?? meData.user.role ?? "viewer";
        setMe({ id: meData.user.id, role: ent === "admin" ? "admin" : ent === "builder" ? "builder" : "viewer" });
      } catch {
        /* the scope row simply stays read-only */
      }
    })();
    void (async () => {
      try {
        const data = await apiFetch<{ proposals: ProposalListItem[] }>(
          `/api/tenant/optimize/models/${model.id}/proposals`
        );
        setProposals(data.proposals);
      } catch {
        /* the versions list simply stays absent */
      }
    })();
  }, [model.id]);

  /** One CAS-carried PATCH; `stale_write` surfaces as "changed elsewhere". */
  async function patchModel(body: Record<string, unknown>) {
    try {
      const saved = await apiFetch<{ model: Model }>(`/api/tenant/optimize/models/${model.id}`, {
        method: "PATCH",
        body: { expected_updated_at: model.updated_at, ...body },
      });
      props.onModelChanged(saved.model);
      return true;
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "stale_write") {
        toast.push({ kind: "error", message: "This model changed elsewhere — reload to pick up the latest." });
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
      return false;
    }
  }

  /** Blur-save one solve budget (seconds on screen → ms stored; blank = default). */
  async function saveBudget(field: "solve_time_budget_ms" | "interactive_budget_ms", value: string) {
    const trimmed = value.trim();
    const seconds = trimmed === "" ? null : Number(trimmed);
    if (seconds !== null && (!Number.isFinite(seconds) || seconds < 1)) {
      toast.push({ kind: "error", message: "A budget is whole seconds, at least 1 — or blank for the default." });
      return;
    }
    const ms = seconds === null ? null : Math.round(seconds * 1000);
    if (model[field] === ms) return;
    await patchModel({ [field]: ms });
  }

  async function toggleStatus() {
    setStatusBusy(true);
    await patchModel({ status: model.status === "active" ? "disabled" : "active" });
    setStatusBusy(false);
  }

  /** The one solve gesture the board lacks: run the bound pipelines first. */
  async function refreshInputsAndSolve() {
    setSolveBusy(true);
    try {
      await apiFetch(`/api/tenant/optimize/models/${model.id}/solve`, {
        method: "POST",
        body: { kind: "solve", refresh_inputs: true },
      });
      toast.push({ kind: "success", message: "Solve started — the board shows it land." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSolveBusy(false);
    }
  }

  return (
    <div className="card" style={{ background: "var(--bg-subtle)" }}>
      <strong className="muted">Administration</strong>
      <div className="help">
        Operational settings for this model. The model's shape above is authored by Atlas — ask Atlas to
        change what it schedules or how it prices changeovers.
      </div>
      <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
        <div className="inputRow" style={{ gap: 8 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Standing solve budget (seconds)</span>
            <input
              className="input"
              type="number"
              min={1}
              placeholder="30 (default)"
              value={standingBudgetS}
              onChange={(e) => setStandingBudgetS(e.target.value)}
              onBlur={() => void saveBudget("solve_time_budget_ms", standingBudgetS)}
            />
            <div className="help">Used by the nightly scheduled solve. Blank = the default.</div>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Interactive budget (seconds)</span>
            <input
              className="input"
              type="number"
              min={1}
              placeholder="45 (default)"
              value={interactiveBudgetS}
              onChange={(e) => setInteractiveBudgetS(e.target.value)}
              onBlur={() => void saveBudget("interactive_budget_ms", interactiveBudgetS)}
            />
            <div className="help">Used when someone at the board re-solves or plays a what-if. Blank = 45 seconds.</div>
          </label>
        </div>

        <div className="inputRow" style={{ gap: 8, alignItems: "center" }}>
          {model.status === "active" ? (
            <span className="pill pillOk">Active</span>
          ) : (
            <span className="pill pillBad">Disabled</span>
          )}
          <button className="btn btnSm" onClick={() => void toggleStatus()} disabled={statusBusy}>
            {model.status === "active" ? "Disable" : "Activate"}
          </button>
          <span className="help" style={{ margin: 0 }}>
            {model.status === "active"
              ? "Solves run and the board is available."
              : "Solves do not run. Only builders and admins see the model on the Optimize list."}
          </span>
        </div>

        <div className="inputRow" style={{ gap: 8, alignItems: "center" }}>
          <button
            className="btn btnSm"
            onClick={() => void refreshInputsAndSolve()}
            disabled={solveBusy || model.status !== "active"}
            title="Runs the bound data pipelines first — live data, at their wall time."
          >
            {solveBusy ? "Starting…" : "Refresh inputs & solve"}
          </button>
          <span className="help" style={{ margin: 0 }}>
            The board's Re-solve uses data already in place; this runs the bound pipelines first.
          </span>
        </div>

        {proposals.length > 0 ? (
          <div>
            <span className="label">Plan versions</span>
            <div className="tableWrap">
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Landed</th>
                    <th>Setup hours saved</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <Link to={`/app/optimize/proposals/${p.id}`}>v{p.version}</Link>
                      </td>
                      <td>{formatTimestamp(p.created_at)}</td>
                      <td>{p.diff?.hours_saved != null ? `${p.diff.hours_saved} h` : <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {me ? (
          <ScopeHeader
            entityKind="optimize_model"
            entityId={model.id}
            scope={model.scope}
            ownerGroupId={model.owner_group_id}
            ownerGroupName={model.owner_group_name ?? null}
            ownerAccountId={model.owner_account_id}
            callerAccountId={me.id}
            callerRole={me.role}
            callerGroupIds={callerGroups.map((g) => g.id)}
            onScopeChanged={props.onReload}
            disableClone
            destructiveConfirmation={{
              value: model.name,
              label: "Type the model's name to confirm.",
              detail:
                "Deleting removes the model, its solves, its proposals and its disruption history. This cannot be undone.",
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

// ── the model home shell ───────────────────────────────────────────────────

export function OptimizeModelHome(props: { entitlement?: string }) {
  const { modelId = "" } = useParams<{ modelId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = modelHomeTabFromSearch(searchParams.get("tab"));
  const canManage = canManageModels(props.entitlement);

  const [model, setModel] = useState<Model | null>(null);
  const [template, setTemplate] = useState<TemplateProjection | null>(null);
  const [bindings, setBindings] = useState<ModelBindingView[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Addendum 5 — the Next-up tab's jump: the focus key the board view opens
  // expanded (opaque here; the view minted it). Cleared when the Board tab is
  // chosen by hand.
  const [boardFocus, setBoardFocus] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const detail = await apiFetch<{ model: Model; template: TemplateProjection | null; bindings?: ModelBindingView[] }>(
        `/api/tenant/optimize/models/${modelId}`
      );
      setModel(detail.model);
      setTemplate(detail.template);
      setBindings(detail.bindings ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [modelId]);

  useEffect(() => {
    setModel(null);
    void load();
  }, [load]);

  // The browser tab follows the breadcrumb: "{model name} / Board|Next up|Model".
  const modelName = model?.name ?? null;
  useEffect(() => {
    if (!modelName) return;
    document.title = `${modelName} / ${tab === "model" ? "Model" : tab === "nextup" ? "Next up" : "Board"}`;
    return () => {
      document.title = "Atlas Console";
    };
  }, [modelName, tab]);

  if (error) {
    return (
      <PageLayout title="Optimize">
        <ErrorState detail={error} />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <Link className="breadcrumbLink" to="/app/optimize">
            Optimize
          </Link>
          <span className="breadcrumbSep">/</span>
          <span>{model?.name ?? "…"}</span>
        </span>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div className="tabs">
          <button
            type="button"
            className={`tab${tab === "board" ? " tabActive" : ""}`}
            onClick={() => {
              setBoardFocus(null);
              setSearchParams({});
            }}
          >
            Board
          </button>
          <button
            type="button"
            className={`tab${tab === "nextup" ? " tabActive" : ""}`}
            onClick={() => setSearchParams({ tab: "next-up" })}
          >
            Next up
          </button>
          <button
            type="button"
            className={`tab${tab === "model" ? " tabActive" : ""}`}
            onClick={() => setSearchParams({ tab: "model" })}
          >
            Model
          </button>
        </div>

        {tab === "board" ? (
          <OptimizeBoardFrame modelId={modelId} initialFocus={boardFocus} />
        ) : tab === "nextup" ? (
          <OptimizeNextUpFrame
            modelId={modelId}
            onOpenBoard={(focus) => {
              setBoardFocus(focus);
              setSearchParams({});
            }}
          />
        ) : model ? (
          <ModelDescription
            model={model}
            template={template}
            bindings={bindings}
            canManage={canManage}
            onModelChanged={setModel}
            onReload={load}
          />
        ) : (
          <LoadingState />
        )}
      </div>
    </PageLayout>
  );
}
